"use client";

import { ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ProductosSLidos() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const services = [{
    code: "SOL / 01",
    title: "Barras Redondas",
    description: "Acero de alta ductilidad para aplicaciones industriales y de construcción pesada.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/barras_redondas_acero.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/barras_redondas_acero.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/barras_redondas_acero.png-wvc-sizes",
    features: ["Grado estructural", "Corte a medida", "Alta resistencia"]
  }, {
    code: "SOL / 02",
    title: "Cuadrados Sólidos",
    description: "Perfiles macizos ideales para herrería artística y refuerzos estructurales de precisión.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/cuadrados_solidos_metal.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/cuadrados_solidos_metal.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/cuadrados_solidos_metal.png-wvc-sizes",
    features: ["Acabado uniforme", "Versatilidad total", "Stock permanente"]
  }, {
    code: "SOL / 03",
    title: "Soleras de Acero",
    description: "Placas rectangulares de acero laminado en caliente para soportes y marcos industriales.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/soleras_acero_laminado.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/soleras_acero_laminado.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/soleras_acero_laminado.png-wvc-sizes",
    features: ["Laminado en caliente", "Bordes definidos", "Calidad certificada"]
  }];
  return <section data-section-id="818" id="productos-sólidos" className="relative bg-background text-foreground py-20 md:py-32 overflow-hidden">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{
      backgroundImage: "radial-gradient(circle, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "24px 24px"
    }} />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-16 lg:mb-24 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-7">
            <div className="font-mono text-xs tracking-[0.2em] uppercase text-primary mb-6">
              03 / Capacidades
            </div>
            <h2 className="font-default font-bold tracking-tight text-foreground text-[2.5rem] sm:text-5xl lg:text-[4.5rem] leading-[0.95]">
              Solidez pura.
              <br />
              <span className="italic font-default font-normal text-primary">Calidad Kalisch.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pb-3">
            <p className="font-default text-base lg:text-lg text-muted-foreground leading-relaxed mb-6">
              Desde proyectos de herrería artesanal hasta infraestructuras industriales de gran escala, nuestros productos sólidos ofrecen la resistencia mecánica que su obra exige.
            </p>
            <Link to="/SolidosProductosPage" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.15em] uppercase text-primary border-b border-primary pb-1 hover:gap-3 transition-all">
              Ver catálogo completo
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, i) => <div data-index={i} key={i} className={`group border border-border bg-card flex flex-col overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-lg rounded-lg ${i === 1 ? "md:translate-y-12" : ""}`} style={{
          aspectRatio: "auto"
        }}>
              {}
              <div className="relative w-full overflow-hidden aspect-[4/3]">
                <img src={service.image} data-wvc-srcset={service.srcset} data-wvc-sizes={service.sizes} alt={service.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
              </div>

              {}
              <div className="flex-1 flex flex-col p-8">
                <div className="flex items-start justify-between mb-8">
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                    {service.code}
                  </span>
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-primary/20 text-accent transition-all duration-500 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:rotate-45 group-hover:border-accent">
                    <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>

                <h3 className="font-default text-2xl md:text-[28px] font-bold tracking-tight text-foreground leading-tight mb-3">
                  {service.title}
                </h3>
                <p className="font-default text-sm text-muted-foreground leading-relaxed mb-8">
                  {service.description}
                </p>

                <ul className="mt-auto flex flex-col sm:flex-row sm:flex-wrap gap-x-5 gap-y-2 pt-6 border-t border-border">
                  {service.features.map((feature, fi) => <li data-index={fi} key={fi} className="flex items-center gap-2 font-mono text-[11px] tracking-wide uppercase text-foreground/80">
                      <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-md bg-accent" />
                      {feature}
                    </li>)}
                </ul>
              </div>
            </div>)}
        </div>

        {}
        <div className="mt-20 md:mt-28 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-10 border-t border-border">
          <p className="font-default text-sm text-muted-foreground max-w-md">
            ¿Necesita especificaciones técnicas o medidas especiales? Nuestro equipo de expertos está listo para asesorarle.
          </p>
          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary-600 h-12 px-8 rounded-md font-mono text-xs tracking-[0.15em] uppercase shadow-md">
            <Link to="/CatalogPage">
              Solicitar Cotización
              <ArrowUpRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}